import reduce from 'lodash/reduce';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import set from 'lodash/set';

import { getAccountService, deleteDoc } from '../../../../services/apiCommon';
import * as c from '../constants';

export async function getTemplateById(feathersApp, id) {
  try {
    if (!id) {
      return null;
    }

    const service = getAccountService(feathersApp, c.DOC_TYPE_HOTKEY_TEMPLATE);

    return await service.get(id);
  } catch (err) {
    console.error('@getTemplateById: ', err.message || err);
    throw err;
  }
}

export async function deleteTemplateById(feathersApp, id) {
  try {
    return deleteDoc(feathersApp, {
      id,
      docType: c.DOC_TYPE_HOTKEY_TEMPLATE
  });
  } catch (err) {
    console.error('@deleteTemplate: ', err.message || err);
    throw err;
  }
}

export async function getTemplateRootTabs(feathersApp, templateId) {
  try {
    if (!templateId) {
      return [];
    }

    const service = getAccountService(feathersApp, c.DOC_TYPE_HOTKEY_TEMPLATE_GROUP);

    const query = {
      parent_hotkey_template_id: templateId,
      no_parent: true,
      limit: 100
    };

    const { data } = (await service.find({ query })) || [];

    return data;
  } catch (err) {
    console.error('@getTemplateRootTabs: ', err.message || err);
    return [];
  }
}

export async function getHotkeyRootTabs(feathersApp) {
  try {
    const service = getAccountService(feathersApp, c.DOC_TYPE_HOTKEY_GROUP);

    const query = {
      no_parent: true,
      parent: null,
      limit: 100
    };

    const { data } = (await service.find({ query })) || [];

    return data;
  } catch (err) {
    console.error('@getHotkeyRootTabs: ', err.message || err);
    return [];
  }
}

export async function getTemplateChildren(feathersApp, parent) {
  try {
    const { hotkey_list } = parent || {};
    const itemIds = reduce(hotkey_list, (r, h) => {
      if (!h || !h.item_id) {
        return r;
      }
      return r.concat(h.item_id);
    }, []);

    if (isEmpty(itemIds)) {
      return [];
    }

    const service = getAccountService(feathersApp, c.DOC_TYPE_ITEM);
    const query = {
      items: itemIds,
      check_active: true,
      active: true,
      limit: 100
    };

    const { data: items } = await service.find({ query });

    if (isEmpty(items)) {
      return [];
    }

    const indexed = keyBy(hotkey_list, 'item_id');

    return items.map(item => {
      const { id } = item;
      const {
        name = null,
        image_url = null,
        active = false,
        position = null,
        is_template_group = false
      } = indexed[id];

      return {
        ...item,
        name,
        active,
        position,
        image_url,
        is_template_group
      };
    });
  } catch (err) {
    console.error('@getTemplateChildren: ', err.message || err);
    return [];
  }
}

export async function getHotkeyChildrenByItem(feathersApp, parent) {
  try {
    const { is_template_group, key } = parent;

    if (is_template_group) {
      return [];
    }

    let service = getAccountService(feathersApp, c.DOC_TYPE_HOTKEY_GROUP_ITEM);

    const hotkeyQuery = {
      hotkey_group_id: key,
      limit: 100
    };

    const hotkeyDocs = await service.find({ query: hotkeyQuery });

    if (isEmpty(hotkeyDocs)) {
      return [];
    }

    const itemQuery = {
      items: map(hotkeyDocs, 'hotkey_group_item_id'),
      limit: 100
    };

    service = getAccountService(feathersApp, c.DOC_TYPE_ITEM);
    const { data } = (await service.find({ query: itemQuery })) || [];

    return data;
  } catch (err) {
    console.error('@getHotkeyChildrenByItem: ', err.message || err);
    return [];
  }
}

export async function getHotkeyChildrenByClass(feathersApp, parent) {
  try {
    const { classification_id } = parent || {};

    if (!classification_id) {
      return [];
    }

    const service = getAccountService(feathersApp, c.DOC_TYPE_ITEM);
    const query = {
      active: true,
      classification: [classification_id],
      limit: 100
    };
    const { data } = await service.find({ query });

    return map(data, d => set(d, 'active', false));
  } catch (err) {
    console.error('@getHotkeyChildrenByClass: ', err.message || err);
    return [];
  }
}

export async function getTemplateChildrenGroup(feathersApp, parent, templateId) {
  try {
    const { id, level } = parent;

    const service = getAccountService(feathersApp, c.DOC_TYPE_HOTKEY_TEMPLATE_GROUP);

    const query = {
      parent_id: id,
      level: level + 1,
      parent_hotkey_template_id: templateId,
      is_group: true,
      limit: 100,
    };

    const { data } = await service.find({ query });

    return data.map(item => ({
      ...item,
      parent
    }));
  } catch (err) {
    console.error('@getTemplateChildrenGroup: ', err.message || err);
    return [];
  }
}

export async function getHotkeyChildrenGroup(feathersApp, parent) {
  try {
    const { key } = parent;

    const service = getAccountService(feathersApp, c.DOC_TYPE_HOTKEY_GROUP);

    const query = {
      parent: key,
      limit: 100
    };

    const { data } = await service.find({ query });

    return data;
  } catch (err) {
    console.error('@getHotkeyChildrenGroup: ', err.message || err);
    return [];
  }
}

export async function getTabHotkeyChildrenGroup(feathersApp, tab) {
  try {
    const { group } = tab;
    const { _id: groupId } = group;
    const service = getAccountService(feathersApp, c.DOC_TYPE_HOTKEY_GROUP);

    const query = {
      parent_id: groupId,
      limit: 100
    };

    const { docs } = await service.find({ query });

    return docs;
  } catch (err) {
    console.error('@getTabHotkeyChildrenGroup: ', err.message || err);
    return [];
  }
}

export async function getItemByIds(feathersApp, itemIds) {
  try {
    const service = getAccountService(feathersApp, c.DOC_TYPE_ITEM);

    const query = {
      items: itemIds,
      check_active: true,
      active: true,
      limit: 100
    };

    const { data } = (await service.find({ query })) || [];

    return data;
  } catch (err) {
    console.error('@getItemByIds: ', err.message || err);
    return [];
  }
}
